import { isCurrentTimeWithinDateRange } from '../../utilities/helper';
import { deliveryEstimate } from '../../utilities/deliveryEstimate';

/* THE CART MODEL — one owner for the grouping and the arithmetic.
   Ported from SP-main js/storefront-shell.js: `cartGroups()`, `cartTotals()`,
   `brandShipCost` and `brandShipNote`.

   One owner for the arithmetic is the whole reason this file exists: three
   pages each doing their own sums is three chances for the total a shopper
   agreed to and the total they were charged to differ. The drawer and the cart
   page used to carry byte-for-byte copies of `getDiscountValue`,
   `isProductUnavailable`, `calcBrandTotal` and `calcTotal`.

   A group is a `brand_user`, not a vendor (see `cartGroups`): a group's header
   promises a shipping cost and a dispatch time, and both hang off `brand_user`
   in this API. Grouping finer would print a shipping charge per vendor that
   checkout does not levy.

   ⚠ The shipping total here must equal the one checkout charges. It is derived
   as `OrderListing` derives it (src/components/OrderListing/index.js l.59) —
   `shipping_rate.shipping_cost` summed once per `brand_user`. If that moves,
   this moves with it. */

export const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

/* Carried over verbatim from the two cart files. A variant price can be a range
   ("20.00-249.90"); the discount applies to each end and callers read the low
   end, as both surfaces already did. */
export const getDiscountValue = (price, brandDiscount) => {
  const discount = brandDiscount?.discount_percentage
    ? parseFloat(brandDiscount.discount_percentage)
    : 0;
  const active = discount
    && isCurrentTimeWithinDateRange(brandDiscount?.start_date, brandDiscount?.end_date);
  const pct = active ? discount : 0;

  const prices = String(price ?? '').split('-').map((v) => parseFloat(v));
  return prices
    .map((v) => (pct ? (v - (v * pct) / 100).toFixed(2) : (Number(v) || 0).toFixed(2)))
    .join('-');
};

export const unitPrice = (item) =>
  parseFloat(
    getDiscountValue(
      item?.product_variants?.msrp_price,
      item?.products?.user?.brand_discount,
    ).split('-')[0],
  ) || 0;

export const wasDiscounted = (item) =>
  parseFloat(item?.product_variants?.msrp_price) !== unitPrice(item);

/* Unchanged in substance — removing any condition would put an unbuyable line
   through checkout. The original repeated five of these checks further down the
   same function and ended with a branch whose two arms both returned false; the
   set of products this returns true for is identical. */
export const isProductUnavailable = (group, item) => {
  const options = item?.products?.product_options;
  if (
    options?.length < 2
    && !options?.[0]?.value?.includes(item?.product_variants?.title)
  ) return true;

  if (item?.product_variants?.inventory_status !== 'IN_STOCK') return true;
  if (group?.brand_user?.access_token?.is_active !== true) return true;
  if (item?.products?.product_status?.status !== 'active') return true;
  if (item?.product_variants?.status !== '1') return true;
  if (item?.products?.store_products?.length === 0) return true;
  // if (!item?.products?.user?.invitees?.length && !item?.products?.user?.inviters?.length) return true;
  if (item?.product_variants?.allowed_inventory_quantity === 0) return true;
  if (!item?.products || item.products.status !== 1) return true;
  if (item?.product_variants?.is_deleted) return true;

  return false;
};

/* The inventory ceiling for a line, or null where the brand does not track
   stock — null means "no known limit", which is not zero. */
export const stockCeiling = (item) => (
  item?.product_variants?.inventory_management
    ? (item?.product_variants?.allowed_inventory_quantity ?? null)
    : null
);

/* THE GROUP NAME
   ⚠ A vendor and a brand do not share an id. On a multi-brand store the display
   name comes from `shopify_vendor.shopdot_vendor.display_name`; on a
   single-brand one it is `brand_details.store_name`. Reading the wrong one puts
   another brand's name on a parcel. */
export const groupName = (group) => {
  if (!group?.brand_user?.is_multi_brand) {
    return group?.brand_user?.brand_details?.store_name || '';
  }
  const vendors = new Set(
    (group.cart_items || [])
      .map((i) => i?.products?.shopify_vendor?.shopdot_vendor?.display_name)
      .filter(Boolean),
  );
  /* One vendor in the parcel — name it. Several — the store is the only honest
     label for the whole parcel. */
  if (vendors.size === 1) return [...vendors][0];
  return group?.brand_user?.brand_details?.store_name || '';
};

/* A group's identity for a React key. ⚠ Never `id` alone: a vendor's id and a
   brand's id come from different endpoints and can collide, and two children
   under one key makes React reuse the instance — a row repeats while another
   vanishes, with nothing logged. */
export const groupKey = (group, i) => [
  group?.brand_user?.id ?? 'nb',
  group?.cart_items?.[0]?.products?.shopify_vendor?.id ?? 'nv',
  i,
].join(':');

const shippingCost = (group) =>
  parseFloat(group?.brand_user?.brand_details?.shipping_rate?.shipping_cost) || 0;

/* The note carries terms only — the group header already names the brand;
   naming it here too put "$5.00 shipping from PawCo" beside "Ships by PawCo".

   ⛔ There is no free-shipping threshold in this API, so the reference's "Free
   over $40" branch is not ported: `shipRule().freeOver` has no equivalent field.
   A zero cost IS real and reads as "Free shipping". Do not synthesise a
   threshold — it is a claim about what a customer will be charged. */
export const shipNote = (group) => {
  const cost = shippingCost(group);
  return cost === 0 ? 'Free shipping' : `Flat ${money(cost)} shipping`;
};

export const shipEta = (group) => deliveryEstimate(group?.brand_user?.shipping_address);

export const isFreeShipping = (group) => shippingCost(group) === 0;

/* GROUPS
   The API already returns the cart grouped by `brand_user`: this is the shape
   `getCart` hands back and the shape `OrderListing` bills from.

   ⛔ Not `splitMultiBrandCarts`, which splits one store's items per VENDOR.
   Every split group then carried the same `shipping_rate.shipping_cost`, because
   the rate belongs to the store — so a two-vendor store printed "Flat $5.00
   shipping" twice, reading as $10.00 against the $5.00 checkout charges, and
   inflated the parcel count the same way. `groupName` above still surfaces the
   vendor's name when a parcel holds exactly one. */
export const cartGroups = (cartItems = []) =>
  (cartItems || []).map((group, i) => {
    const items = group?.cart_items || [];
    const subtotal = items.reduce(
      (sum, item) => (isProductUnavailable(group, item)
        ? sum
        : sum + unitPrice(item) * item.cart_quantity),
      0,
    );
    return {
      raw: group,
      key: groupKey(group, i),
      name: groupName(group),
      items,
      subtotal,
      shipping: shippingCost(group),
      shipNote: shipNote(group),
      isFree: isFreeShipping(group),
      eta: shipEta(group),
    };
  });

/* ⚠ `tax` is null, not zero: "we cannot know yet" is not "no tax is due". Tax
   needs a delivery address the cart does not have, so the total is an ESTIMATE
   and every caller must label it as one. Never `(t.tax||0)`. */
export const cartTotals = (cartItems = []) => {
  const groups = cartGroups(cartItems);
  const subtotal = groups.reduce((s, g) => s + g.subtotal, 0);
  /* Only parcels with something buyable are charged for or counted — a group
     whose every line is unavailable ships nothing. */
  const live = groups.filter((g) => g.subtotal > 0);
  const shipping = live.reduce((s, g) => s + g.shipping, 0);
  return {
    subtotal,
    shipping,
    tax: null,
    total: subtotal + shipping,
    parcels: live.length,
    groups,
  };
};

export const cartItemCount = (cartItems = []) =>
  (cartItems || []).reduce((n, g) => n + (g?.cart_items?.length || 0), 0);

export const hasUnavailable = (cartItems = []) =>
  (cartItems || []).some((g) => (g?.cart_items || []).some((i) => isProductUnavailable(g, i)));

/* The payload `addToCart` expects for a quantity change. Unchanged in shape,
   lifted out of the two copies that each rebuilt it inline. */
export const quantityPayload = (group, item, quantity) => ({
  brands: [
    {
      brand_user_id: group?.brand_user?.id,
      products: [
        {
          product_id: item?.products?.id,
          variants: [{ variant_id: item?.product_variants?.id, quantity }],
          customizableNote: item?.customizable_note,
        },
      ],
    },
  ],
});
